import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Wallet } from '@types';
import ConnectedWallet from './ConnectedWallet';
import WalletIcon from '@components/WalletIcon';
import useWallets from '@hooks/useWallets';
import useHasConnectedWallet from '@hooks/useHasConnectedWallet';
import Button from './Button';
import StackView from './StackView';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import Icon from './Icon';
import IconButton from './IconButton';
import OutsideClickHandler from 'react-outside-click-handler';
import WalletsMobileMenu from './WalletsMobileMenu';
import clsx from 'clsx';
import useSendTransactionContext from '@hooks/useSendTransactionContext';
import useGenericPopup from '@hooks/useGenericPopup';
import useNetworkEnv from '@hooks/useNetworkEnv';
import DropdownMenu from './DropdownMenu';
import NetworkEnvMobileMenu from './NetworkEnvMobileMenu';
import BetaBadge from '@components/BetaBadge';
import { getUrlWithBasePath } from '@lib/utils';

const isShowNetworkEnvSwitch =
  process.env.NEXT_PUBLIC_SHOW_NETWORK_ENV_SWITCH === 'true';

const NotificationDot = ({ className }: { className?: string }) => (
  <div className={clsx('bg-red w-1 h-1 rounded-full', className)} />
);

const networkOptions = [
  {
    label: 'Mainnet Bridge',
    value: false,
  },
  {
    label: 'Testnet Bridge',
    value: true,
    disabled: false,
  },
];

const NavBar = () => {
  const { wallets } = useWallets();
  const [isTestnet, setTestnet] = useNetworkEnv();
  const getIsConnected = useHasConnectedWallet();
  const { showWrongNetworkPopup, isOpen: isPopupOpen } = useGenericPopup();
  const { bridgeTxNotifications } = useSendTransactionContext();
  const [isShowWalletsMobileMenu, setIsShowWalletsMobileMenu] = useState(false);
  const [isShowNetworkEnvMobileMenu, setIsShowNetworkEnvMobileMenu] =
    useState(false);
  const { onShowWalletConnection } = useTransferAssetFlowContext();

  const hasBridgeTxNotification = useMemo(
    () => bridgeTxNotifications.some(({ isHidden }) => !isHidden),
    [bridgeTxNotifications]
  );

  const renderNetworkToggle = () => (
    <>
      <div className="hidden tablet:block">
        <DropdownMenu
          value={isTestnet}
          options={networkOptions}
          onChange={setTestnet}
          className="w-[180px] text-body"
        >
          {(props) => (
            <Button size="small" {...props}>
              {isTestnet ? 'Testnet' : 'Mainnet'}
              <Icon.ArrowDown className="text-iconMedium ml-1" />
            </Button>
          )}
        </DropdownMenu>
      </div>
      <IconButton
        className="tablet:hidden -m-1"
        onClick={() => setIsShowNetworkEnvMobileMenu(true)}
      >
        <Icon.Mainnet
          className={clsx('text-iconMedium', {
            'text-blue': !isTestnet,
            'text-white': isTestnet,
          })}
        />
      </IconButton>
    </>
  );

  const renderHistoryLink = () => (
    <>
      <Link href="/history">
        <a className="text-white text-body px-3 hidden desktop:block">
          History
        </a>
      </Link>
      <Link href="/history">
        <a className="text-white text-iconMedium hidden tablet:inline desktop:hidden">
          <IconButton className="-m-1">
            <Icon.History />
          </IconButton>
        </a>
      </Link>
    </>
  );

  const renderLogoImg = () => (
    <>
      <img
        className="h-[40px] hidden desktop:block"
        src={getUrlWithBasePath('/assets/images/nav-bar-logo.svg')}
        alt="nav-bar-logo"
      />
      <img
        className="h-[30px] desktop:hidden"
        src={getUrlWithBasePath('/assets/images/nav-bar-logo-mobile.svg')}
        alt="nav-bar-logo"
      />
    </>
  );

  const renderWallets = () => (
    <>
      <div className="tablet:hidden relative">
        {hasBridgeTxNotification && (
          <NotificationDot className="absolute top-1 right-0" />
        )}
        {wallets.length === 0 ? (
          <IconButton
            className="-m-1 text-iconMedium text-white"
            onClick={onShowWalletConnection}
          >
            <Icon.Wallet />
          </IconButton>
        ) : wallets.filter(({ active }) => active).length === 0 ? (
          <IconButton
            className="-m-1 text-iconMedium text-red"
            onClick={() => {
              showWrongNetworkPopup();
              setIsShowWalletsMobileMenu((prev) => !prev);
            }}
          >
            <Icon.Network />
          </IconButton>
        ) : (
          <IconButton
            className="-m-1 text-iconMedium text-white"
            onClick={() => {
              setIsShowWalletsMobileMenu((prev) => !prev);
            }}
          >
            <WalletIcon variant={wallets?.[0].type} idPrefix="nav-bar" />
          </IconButton>
        )}
      </div>
      <StackView
        direction="horizontal"
        spacing={3}
        className="items-center hidden tablet:flex"
      >
        {wallets.length === 0 ? (
          <Button size="small" onClick={onShowWalletConnection}>
            Connect Wallet
          </Button>
        ) : (
          wallets.map((wallet) => (
            <ConnectedWallet key={wallet.type} wallet={wallet} />
          ))
        )}
      </StackView>
    </>
  );

  const renderLeft = () => (
    <StackView direction="horizontal" spacing={3} className="items-center">
      <Link href="/">
        <a>{renderLogoImg()}</a>
      </Link>
      <BetaBadge />
    </StackView>
  );

  const renderRight = () => (
    <StackView direction="horizontal" spacing={3} className="items-center">
      {getIsConnected(wallets) && renderHistoryLink()}
      {isShowNetworkEnvSwitch && renderNetworkToggle()}
      {renderWallets()}
    </StackView>
  );

  return (
    <OutsideClickHandler
      display="block"
      disabled={
        (!isShowWalletsMobileMenu && !isShowNetworkEnvMobileMenu) || isPopupOpen
      }
      onOutsideClick={() => {
        setIsShowWalletsMobileMenu(false);
        setIsShowNetworkEnvMobileMenu(false);
      }}
    >
      <nav className="relative bg-dark h-[70px] px-2 tablet:px-4 flex items-center justify-between">
        {renderLeft()}
        {renderRight()}
        {isShowWalletsMobileMenu && (
          <div className="absolute top-full left-0 w-full z-dropdown px-2">
            {/* this position relative div is for the z-dropdown to work  */}
            <div className="relative">
              <WalletsMobileMenu
                onMenuItemClick={() => setIsShowWalletsMobileMenu(false)}
              />
            </div>
          </div>
        )}
        {isShowNetworkEnvMobileMenu && (
          <div className="absolute top-full left-0 w-full z-dropdown px-2">
            {/* this position relative div is for the z-dropdown to work  */}
            <div className="relative">
              <NetworkEnvMobileMenu
                options={networkOptions}
                onMenuItemClick={() => setIsShowNetworkEnvMobileMenu(false)}
              />
            </div>
          </div>
        )}
      </nav>
    </OutsideClickHandler>
  );
};

export default NavBar;
